import React, { useState, useEffect, useLayoutEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { pushToast } from '../Toaster/Toaster.slice'
import { ExclamationCircleIcon } from '@heroicons/react/24/solid'
import classnames from 'classnames'
import { Formik, Form, Field } from 'formik'
import * as Yup from 'yup'
import { useAppDispatch } from '../../shared/redux/hooks'
import Input from './Input'

export interface InlineEditInputProps {
  name: string
  label: string
  required?: boolean
  validationSchema?: Yup.StringSchema
  initialValue?: string
  /** When set, view mode shows this instead of the raw value (edit still uses initialValue). */
  displayValue?: string
  className?: string
  hideLabel?: boolean
  linkTemplate?: string
  /** Classes for the view-mode link when `linkTemplate` is set. */
  linkClassName?: string
  formatter?: (value: string) => string
  /** Size the control to its content instead of stretching to 100% width. */
  fitContent?: boolean
  /** Smaller control height for dense metadata rows (e.g. sticky page headers). */
  compact?: boolean
  /** Return `false` to abort success UI (e.g. opened a confirm modal instead of saving). */
  handleSubmit: (value: string | null) => Promise<void | false>
}

export const InlineEditInput: React.FC<InlineEditInputProps> = (props) => {
  const {
    name,
    label,
    validationSchema,
    required,
    initialValue,
    displayValue,
    handleSubmit,
    className,
    hideLabel,
    linkTemplate,
    linkClassName,
    formatter,
    fitContent = false,
    compact = false,
  } = props
  /** Shared height for view + edit so switching modes does not resize the parent. */
  const controlHeight = compact ? 'h-5 max-h-5' : 'h-10 max-h-10'
  const viewHitArea = compact
    ? 'inline-flex h-5 max-h-5 min-w-0 max-w-full items-center gap-1 overflow-hidden rounded px-1'
    : 'inline-flex h-10 max-h-10 min-w-[4.5rem] max-w-full items-center gap-1 overflow-hidden rounded-md px-1.5'
  const inputHeightClass = compact
    ? '!py-0 !leading-5 h-5 max-h-5 text-xs'
    : '!py-1.5 !leading-5 h-10 max-h-10'
  const dispatch = useAppDispatch()
  const [isEditing, setEditing] = useState(false)
  const [editedValue, setEditedValue] = useState<string | undefined>(initialValue)
  const wrapperRef = useRef<HTMLDivElement | null>(null)
  const inputRef = useRef<HTMLInputElement | null>(null)
  const [overlayPosition, setOverlayPosition] = useState({ top: 0, buttonLeft: 0, errorLeft: 0 })

  useEffect(() => {
    setEditedValue(initialValue)
  }, [initialValue])

  const viewLabel = (() => {
    if (!editedValue) return null
    if (displayValue != null && displayValue !== '') return displayValue
    return formatter != null ? formatter(editedValue) : editedValue
  })()

  // Focus + position overlays once when entering edit mode (never from ref callbacks — that loops).
  useLayoutEffect(() => {
    if (!isEditing) return
    const el = inputRef.current
    if (el == null) return
    el.focus({ preventScroll: true })
    const rect = el.getBoundingClientRect()
    setOverlayPosition({
      top: rect.bottom + 4,
      buttonLeft: rect.right - 80,
      errorLeft: rect.left,
    })
  }, [isEditing])

  // Close when the page scrolls vertically (ignore tiny autofocus scrolls)
  useEffect(() => {
    if (!isEditing) return

    const scrollPositions = new Map<EventTarget, number>()

    const handleScroll = (event: Event) => {
      const target = event.target as HTMLElement | Document
      const scrollElement = target === document ? document.documentElement : (target as HTMLElement)
      const currentScrollY = scrollElement === document.documentElement ? window.scrollY : scrollElement.scrollTop

      if (!scrollPositions.has(target)) {
        scrollPositions.set(target, currentScrollY)
        return
      }

      const initialY = scrollPositions.get(target)!
      if (Math.abs(currentScrollY - initialY) > 10) {
        setEditing(false)
      }
    }

    window.addEventListener('scroll', handleScroll, true)
    return () => {
      window.removeEventListener('scroll', handleScroll, true)
    }
  }, [isEditing])

  useEffect(() => {
    function handleClickOutside(event: MouseEvent): void {
      if (wrapperRef.current !== null && !wrapperRef.current.contains(event.target as Node)) {
        setEditing(false)
      }
    }

    if (isEditing) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
      }
    }
  }, [isEditing])

  return (
    <div className={classnames(fitContent && 'inline-block max-w-full', className)}>
      {!(hideLabel ?? false) && (
        <div className="mb-1 font-medium color-base text-slate-700 dark:text-slate-400">{label}</div>
      )}

      {/* Height lock on the control row only — validation text is portaled outside. */}
      <div className={classnames('relative', controlHeight, fitContent ? 'w-auto' : 'w-full')}>
        {!isEditing && (
          <div
            className={classnames('flex items-center overflow-hidden', controlHeight, fitContent ? 'w-auto' : 'w-full')}
            onClick={() => setEditing(true)}
          >
            <span
              className={classnames(
                viewHitArea,
                'cursor-pointer transition-colors hover:bg-indigo-50 dark:hover:bg-indigo-900'
              )}
            >
              <span
                className={classnames(
                  !editedValue && 'text-slate-400 dark:text-slate-500',
                  fitContent ? 'whitespace-nowrap' : 'min-w-0 truncate'
                )}
              >
                {editedValue && viewLabel != null ? (
                  linkTemplate ? (
                    <a
                      onClick={(e) => {
                        e.stopPropagation()
                      }}
                      target="_blank"
                      rel="noreferrer"
                      className={
                        linkClassName ??
                        'underline transition-colors hover:text-indigo-600 dark:hover:text-indigo-400'
                      }
                      href={linkTemplate.replace('@@value@@', editedValue)}
                      title={editedValue}
                    >
                      {viewLabel}
                    </a>
                  ) : (
                    <span title={displayValue != null && displayValue !== '' ? editedValue : undefined}>{viewLabel}</span>
                  )
                ) : (
                  'Not set'
                )}
              </span>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-[0.85em] w-[0.85em] shrink-0 stroke-2 text-indigo-500 dark:text-indigo-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z"
                  clipRule="evenodd"
                />
              </svg>
            </span>
          </div>
        )}

        {isEditing && (
          <Formik
            initialValues={{
              [name]: editedValue,
            }}
            enableReinitialize
            validationSchema={Yup.object().shape({
              [name]: Yup.string()
                .concat(
                  Yup.string()
                    .trim('Must be trimmed')
                    .required(() => (required ?? false ? 'Required' : true))
                )
                .concat(validationSchema ?? Yup.string()),
            })}
            onSubmit={async (values, { setSubmitting, setFieldValue }) => {
              if (formatter !== undefined) {
                setFieldValue(name, formatter(values[name] ?? ''))
                values[name] = formatter(values[name] ?? '')
              }
              try {
                const result = await handleSubmit(values[name] ?? null)
                if (result === false) {
                  setEditing(false)
                  setSubmitting(false)
                  return
                }
                setEditedValue(values[name])
                dispatch(pushToast({ message: `${label} updated` }))
                setEditing(false)
              } catch (error) {
                dispatch(
                  pushToast({
                    type: 'error',
                    message: `Error updating ${label}`,
                    description: `${
                      (error as XMLHttpRequest).response?.data?.message !== undefined
                        ? `${(error as XMLHttpRequest).response?.data?.message as string}:`
                        : 'Error:'
                    } ${(error as { message: string }).message as string}`,
                  })
                )
              }
              setSubmitting(false)
            }}
          >
            {({ values, errors, touched, setFieldValue, handleBlur, validateField }) => (
              <Form className={controlHeight}>
                <div ref={wrapperRef} className={classnames('relative overflow-hidden rounded-md', controlHeight)}>
                  <Field
                    as={Input}
                    type="text"
                    name={name}
                    onKeyDown={(e: React.KeyboardEvent) => {
                      if (e.key === 'Escape') {
                        setEditing(false)
                      }
                    }}
                    className={classnames('px-0 py-0', className)}
                    inputClassName={inputHeightClass}
                    onBlur={(event: React.FocusEvent<HTMLInputElement>) => {
                      if (formatter !== undefined) {
                        setFieldValue(name, formatter(values[name] ?? ''))
                        handleBlur(event)
                        setTimeout(() => {
                          validateField(name)
                        }, 0)
                      }
                    }}
                    autoComplete="off"
                    innerRef={(el: HTMLInputElement | null) => {
                      inputRef.current = el
                    }}
                  />
                  {/* Keep buttons in-form (submit association + click-outside) but escape the clip via fixed. */}
                  <div
                    className="fixed z-[9999]"
                    style={{
                      top: `${overlayPosition.top}px`,
                      left: `${overlayPosition.buttonLeft}px`,
                    }}
                  >
                    <div className="relative">
                      <button
                        type="button"
                        onClick={() => setEditing(false)}
                        className="ml-1.5 rounded border border-slate-200 bg-slate-100 text-slate-400 shadow-md transition-colors hover:bg-slate-200 active:bg-slate-300 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-500 dark:hover:bg-slate-700 dark:active:bg-slate-600"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-6 w-6 stroke-current"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                      <button
                        type="submit"
                        className="ml-1.5 rounded border border-slate-200 bg-slate-100 text-indigo-500 shadow-md transition-colors hover:bg-slate-200 active:bg-indigo-200 dark:border-slate-700 dark:bg-slate-800 dark:text-indigo-600 dark:hover:bg-slate-700 dark:active:bg-indigo-700"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-6 w-6 stroke-current"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </button>
                    </div>
                  </div>
                  {errors[name] !== undefined && touched[name] && errors[name] && (
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                      <ExclamationCircleIcon className="h-5 w-5 text-red-500" aria-hidden="true" />
                    </div>
                  )}
                </div>
                {errors[name] !== undefined &&
                  touched[name] &&
                  createPortal(
                    <p
                      className="pointer-events-none fixed z-[9999] max-w-xs whitespace-normal text-xs text-red-600 dark:text-red-400"
                      id={`${name}-error`}
                      style={{
                        top: `${overlayPosition.top + 32}px`,
                        left: `${overlayPosition.errorLeft}px`,
                      }}
                    >
                      {errors[name]}
                    </p>,
                    document.body
                  )}
              </Form>
            )}
          </Formik>
        )}
      </div>
    </div>
  )
}

export default InlineEditInput
